// HallOfFameSceneTemplate.js - Template-based Hall of Fame
// Uses the beautiful desktop-hall-of-fame.html template design

import BaseMenuScene from "./BaseMenuScene.js";
import { auth, db } from "../firebaseConfig.js";
import { createTemplateHeader, loadTemplateHeaderCSS, bindTemplateHeaderEvents } from "../components/SharedTemplateHeader.js";
import templateOverlayManager from "../utils/TemplateOverlayManager.js";

export default class HallOfFameSceneTemplate extends BaseMenuScene {
    constructor() {
        super({ key: "HallOfFameSceneTemplate" });
        this.domElement = null;
        this.isMobile = window.MobileDetectionService?.isMobile() || window.innerWidth <= 768;
        this.leaderboardData = [];
        this.currentFilters = {
            game: 'all',
            mode: 'all',
            period: 'all'
        };
    }

    async create() {
        console.log('🏆 HallOfFameSceneTemplate: Creating template-based Hall of Fame');
        
        // Call parent create
        super.create();
        
        // Load template CSS
        loadTemplateHeaderCSS();
        
        // Load comprehensive template CSS for beautiful styling
        if (!document.getElementById('all-templates-exact-css')) {
            const link = document.createElement('link');
            link.id = 'all-templates-exact-css';
            link.rel = 'stylesheet';
            link.href = '/ALL_TEMPLATES_EXACT.css?v=' + Date.now();
            document.head.appendChild(link);
            console.log('🎨 Loaded comprehensive template CSS for Hall of Fame');
        }
        
        // Create the interface
        this.createHallOfFameInterface();
        
        // Load data
        await this.loadLeaderboardData();
        
        // Bind events
        bindTemplateHeaderEvents(this);
        this.setupEventListeners();
        
        // Register with overlay manager
        templateOverlayManager.registerOverlay('hall-of-fame-template-overlay', 'HallOfFameSceneTemplate');
        
        this.sceneReady = true;
    }

    createHallOfFameInterface() {
        // Remove existing overlay
        const existingOverlay = document.getElementById('hall-of-fame-template-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement('div');
        overlay.id = 'hall-of-fame-template-overlay';
        overlay.className = 'template-overlay glass-morphism-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: hidden;
            font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            color: #ffffff;
        `;
        
        overlay.innerHTML = this.isMobile ? this.createMobileHallOfFameHTML() : this.createDesktopHallOfFameHTML();
        
        document.body.appendChild(overlay);
        this.domElement = overlay;
        
        console.log('🏆 HallOfFameSceneTemplate: Template interface created');
    }

    createDesktopHallOfFameHTML() {
        return `
            ${createTemplateHeader('Hall of Fame', ['Home', 'Dashboard', 'Hall of Fame'])}
            
            <div class="app-container">
                <!-- Sidebar - KEEP ORIGINAL BEAUTIFUL DESIGN -->
                <aside class="sidebar">
                    <div class="sidebar-header">
                        <div class="logo">
                            <div class="logo-icon">🏆</div>
                            <div class="logo-text">
                                <div>Hall of Fame</div>
                                <div class="logo-subtitle">Competitive Intelligence</div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="filters-section">
                        <h4 style="color: rgba(255, 255, 255, 0.6); font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 16px;">FILTERS</h4>
                        
                        <div class="filter-group" style="margin-bottom: 16px;">
                            <label style="display: block; font-size: 11px; color: rgba(255, 255, 255, 0.6); margin-bottom: 8px; text-transform: uppercase;">GAME</label>
                            <select id="game-filter" class="filter-select">
                                <option value="all">All Games</option>
                                <option value="ppt">PPT Hero</option>
                                <option value="shortcuts">Shortcuts</option>
                                <option value="tech">Classify Tech</option>
                            </select>
                        </div>
                        
                        <div class="filter-group" style="margin-bottom: 16px;">
                            <label style="display: block; font-size: 11px; color: rgba(255, 255, 255, 0.6); margin-bottom: 8px; text-transform: uppercase;">MODE</label>
                            <select id="mode-filter" class="filter-select">
                                <option value="all">All Modes</option>
                                <option value="solo">Solo</option>
                                <option value="team">Team</option>
                            </select>
                        </div>
                    </div>
                    
                    <div class="stats-section">
                        <h4 style="color: rgba(255, 255, 255, 0.6); font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 16px;">GLOBAL STATS</h4>
                        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                            <span style="font-size: 12px; color: rgba(255, 255, 255, 0.6);">Active Users</span>
                            <span style="font-size: 12px; font-weight: 600;" id="active-users-count">847</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                            <span style="font-size: 12px; color: rgba(255, 255, 255, 0.6);">Verified Firms</span>
                            <span style="font-size: 12px; font-weight: 600;" id="verified-firms-count">68</span>
                        </div>
                        <div style="display: flex; justify-content: space-between;">
                            <span style="font-size: 12px; color: rgba(255, 255, 255, 0.6);">Elite Performers</span>
                            <span style="font-size: 12px; font-weight: 600; color: #00d4ff;" id="elite-performers-count">10</span>
                        </div>
                    </div>
                </aside>
                
                <!-- Main Content -->
                <main class="main-content" style="
                    flex: 1;
                    padding: 48px;
                    overflow-y: auto;
                    height: 100%;
                ">
                    <!-- Podium Section -->
                    <div class="podium-section" style="margin-bottom: 48px;">
                        <h2 style="
                            font-size: 32px;
                            font-weight: 700;
                            margin-bottom: 32px;
                            background: linear-gradient(135deg, #00d4ff 0%, #ffffff 100%);
                            -webkit-background-clip: text;
                            background-clip: text;
                            -webkit-text-fill-color: transparent;
                        ">Top Performers</h2>
                        
                        <div id="podium-container" class="podium-container" style="
                            display: flex;
                            justify-content: center;
                            align-items: end;
                            gap: 24px;
                            margin-bottom: 48px;
                        ">
                            <!-- Podium will be populated by JavaScript -->
                        </div>
                    </div>
                    
                    <!-- Leaderboard Table -->
                    <div class="leaderboard-section">
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;">
                            <h3 style="font-size: 24px; font-weight: 600;">Global Rankings</h3>
                            <div class="live-indicator" style="
                                display: flex;
                                align-items: center;
                                gap: 8px;
                                color: #10b981;
                                font-size: 12px;
                                font-weight: 600;
                            ">
                                <div style="width: 8px; height: 8px; background: #10b981; border-radius: 50%; animation: pulse 2s infinite;"></div>
                                LIVE
                            </div>
                        </div>
                        
                        <div class="leaderboard-table" style="
                            background: rgba(30, 41, 59, 0.6);
                            backdrop-filter: blur(20px);
                            border: 1px solid rgba(255, 255, 255, 0.1);
                            border-radius: 16px;
                            overflow: hidden;
                        ">
                            <div class="table-header" style="
                                display: grid;
                                grid-template-columns: 60px 1fr 120px 100px 80px;
                                gap: 16px;
                                padding: 20px 24px;
                                background: rgba(255, 255, 255, 0.05);
                                border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                                font-size: 12px;
                                font-weight: 600;
                                color: rgba(255, 255, 255, 0.6);
                                text-transform: uppercase;
                                letter-spacing: 0.5px;
                            ">
                                <div>Rank</div>
                                <div>Player</div>
                                <div>Firm</div>
                                <div>Score</div>
                                <div>Trend</div>
                            </div>
                            
                            <div id="leaderboard-content" class="table-content">
                                <!-- Table rows will be populated by JavaScript -->
                            </div>
                        </div>
                    </div>
                </main>
            </div>
            
            <style>
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                
                .podium-position {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                }
                
                .podium-player {
                    background: rgba(30, 41, 59, 0.8);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 16px;
                    padding: 24px;
                    text-align: center;
                    margin-bottom: 16px;
                    min-width: 180px;
                    transition: transform 0.3s ease;
                }
                
                .podium-player:hover {
                    transform: translateY(-4px);
                }
                
                .podium-first .podium-player {
                    border-color: #fbbf24;
                    box-shadow: 0 8px 32px rgba(251, 191, 36, 0.2);
                }
                
                .podium-second .podium-player {
                    border-color: #94a3b8;
                    box-shadow: 0 8px 32px rgba(148, 163, 184, 0.2);
                }
                
                .podium-third .podium-player {
                    border-color: #f97316;
                    box-shadow: 0 8px 32px rgba(249, 115, 22, 0.2);
                }
                
                .podium-avatar {
                    width: 64px;
                    height: 64px;
                    background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0 auto 16px auto;
                }
                
                .podium-name {
                    font-size: 16px;
                    font-weight: 600;
                    margin-bottom: 4px;
                }
                
                .podium-firm {
                    font-size: 12px;
                    color: rgba(255, 255, 255, 0.6);
                    margin-bottom: 8px;
                }
                
                .podium-score {
                    font-size: 18px;
                    font-weight: 700;
                    color: #00d4ff;
                }
                
                .podium-base {
                    width: 120px;
                    height: 60px;
                    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    font-weight: 600;
                    color: rgba(255, 255, 255, 0.8);
                }
                
                .podium-first .podium-base {
                    height: 80px;
                    background: linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(251, 191, 36, 0.1) 100%);
                    border-color: #fbbf24;
                    color: #fbbf24;
                }
                
                .table-row {
                    display: grid;
                    grid-template-columns: 60px 1fr 120px 100px 80px;
                    gap: 16px;
                    padding: 16px 24px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                    transition: background 0.2s ease;
                    align-items: center;
                }
                
                .table-row:hover {
                    background: rgba(255, 255, 255, 0.02);
                }
                
                .rank-badge {
                    width: 32px;
                    height: 32px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    font-weight: 600;
                }
                
                .rank-gold { background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%); color: #0f172a; }
                .rank-silver { background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%); color: #0f172a; }
                .rank-bronze { background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); color: #0f172a; }
                
                .player-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                
                .player-avatar {
                    width: 40px;
                    height: 40px;
                    background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    font-weight: 600;
                    color: #0f172a;
                }
                
                .player-name {
                    font-size: 14px;
                    font-weight: 600;
                }
                
                .firm-badge {
                    background: rgba(0, 212, 255, 0.1);
                    color: #00d4ff;
                    padding: 4px 8px;
                    border-radius: 6px;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .score-display {
                    font-size: 16px;
                    font-weight: 700;
                    color: #00d4ff;
                }
                
                .trend-indicator {
                    font-size: 12px;
                    font-weight: 600;
                }
                
                .trend-up { color: #10b981; }
                .trend-down { color: #ef4444; }
                .trend-same { color: rgba(255, 255, 255, 0.6); }
                
                .filter-select {
                    width: 100%;
                    padding: 8px 12px;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 8px;
                    color: white;
                    font-size: 13px;
                    cursor: pointer;
                }
                
                .filter-select:focus {
                    outline: none;
                    border-color: #00d4ff;
                    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
                }
            </style>
        `;
    }

    createMobileHallOfFameHTML() {
        return `
            ${createTemplateHeader('Hall of Fame', ['Home', 'Dashboard', 'Hall of Fame'])}
            
            <div class="mobile-container" style="
                padding: 24px;
                height: calc(100vh - 80px);
                overflow-y: auto;
            ">
                <!-- Mobile Filters -->
                <div class="mobile-filters" style="
                    background: rgba(30, 41, 59, 0.6);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 16px;
                    padding: 20px;
                    margin-bottom: 24px;
                ">
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
                        <select id="mobile-game-filter" style="
                            padding: 12px;
                            background: rgba(255, 255, 255, 0.05);
                            border: 1px solid rgba(255, 255, 255, 0.1);
                            border-radius: 8px;
                            color: white;
                            font-size: 14px;
                        ">
                            <option value="all">All Games</option>
                            <option value="ppt">PPT Hero</option>
                            <option value="shortcuts">Shortcuts</option>
                            <option value="tech">Classify Tech</option>
                        </select>
                        
                        <select id="mobile-mode-filter" style="
                            padding: 12px;
                            background: rgba(255, 255, 255, 0.05);
                            border: 1px solid rgba(255, 255, 255, 0.1);
                            border-radius: 8px;
                            color: white;
                            font-size: 14px;
                        ">
                            <option value="all">All Modes</option>
                            <option value="solo">Solo</option>
                            <option value="team">Team</option>
                        </select>
                    </div>
                </div>
                
                <!-- Mobile Leaderboard -->
                <div id="mobile-leaderboard-content" class="mobile-leaderboard">
                    <!-- Mobile leaderboard will be populated by JavaScript -->
                </div>
            </div>
        `;
    }

    async loadLeaderboardData() {
        try {
            console.log('🏆 HallOfFameTemplate: Loading leaderboard data with rich functionality...');
            
            // Use the same robust query logic as original Hall of Fame
            let query;
            
            if (this.currentFilters.game === 'ppt') {
                query = db.collection('scores')
                    .where('game', '==', 'PPTHero')
                    .orderBy('score', 'desc')
                    .limit(50);
            } else if (this.currentFilters.game === 'shortcuts') {
                query = db.collection('scores')
                    .where('game', 'in', ['Shortcut', 'MultiplayerShortcut', 'slide wars', 'Slide Wars'])
                    .orderBy('score', 'desc')
                    .limit(50);
            } else if (this.currentFilters.game === 'tech') {
                query = db.collection('scores')
                    .where('game', '==', 'ClassifyTech')
                    .orderBy('score', 'desc')
                    .limit(50);
            } else {
                // For 'all games', use simple orderBy for best performance
                query = db.collection('scores')
                    .orderBy('score', 'desc')
                    .limit(50);
            }
            
            const queryStart = Date.now();
            const snapshot = await query.get();
            const queryTime = Date.now() - queryStart;
            
            console.log(`🏆 HallOfFameTemplate: Firebase query completed in ${queryTime}ms`);
            console.log(`🏆 HallOfFameTemplate: Firebase returned ${snapshot.size} documents`);
            
            let scores = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                console.log(`🏆 HallOfFameTemplate: Processing score:`, {id: doc.id, score: data.score, player: data.player, game: data.game});
                scores.push({
                    id: doc.id,
                    ...data,
                    date: (() => {
                        try {
                            if (data.createdAt && typeof data.createdAt.toDate === 'function') {
                                return data.createdAt.toDate();
                            } else if (data.createdAt instanceof Date) {
                                return data.createdAt;
                            } else if (data.createdAt) {
                                return new Date(data.createdAt);
                            }
                            return new Date();
                        } catch (e) {
                            console.warn('Error processing timestamp for:', data.id || 'unknown', e);
                            return new Date();
                        }
                    })()
                });
            });
            
            // Apply mode filter
            if (this.currentFilters.mode === 'solo') {
                scores = scores.filter(s => !s.teamName);
            }
            
            // Format for display with enhanced firm recognition (copy from original)
            const formattedScores = await Promise.all(scores.slice(0, 25).map(async (data, index) => {
                const playerName = data.player || 'Anonymous';
                // Try to get firm info from userUID lookup
                let firmName = 'Independent';
                try {
                    if (data.userUID) {
                        const userDoc = await db.collection('userProfiles').doc(data.userUID).get();
                        if (userDoc.exists) {
                            const userData = userDoc.data();
                            if (userData.email) {
                                firmName = this.extractFirmFromEmail(userData.email) || 'Independent';
                            }
                        }
                    }
                } catch (error) {
                    console.warn('Firm lookup failed for user:', data.userUID, '- using Independent');
                    firmName = 'Independent';
                }
                
                return {
                    rank: index + 1,
                    name: playerName,
                    firm: firmName,
                    score: data.score || 0,
                    initials: this.generateInitials(playerName),
                    verified: data.verified === true,
                    trend: this.generateTrend(),
                    userUID: data.userUID,
                    rawFirmName: firmName
                };
            }));
            
            this.leaderboardData = formattedScores;
            console.log(`🏆 HallOfFameTemplate: Formatted ${this.leaderboardData.length} scores for display`);
            
            // Update display
            this.updateDisplay();
            
        } catch (error) {
            console.error('🏆 HallOfFameTemplate: Error loading data:', error);
            this.showErrorState();
        }
    }
    
    extractFirmFromEmail(email) {
        if (!email || !email.includes('@')) return null;
        
        const domain = email.split('@')[1].toLowerCase();
        
        // Firm mapping (copy from original)
        const firmMappings = {
            'mckinsey.com': 'McKinsey & Company',
            'bain.com': 'Bain & Company',
            'bcg.com': 'Boston Consulting Group',
            'deloitte.com': 'Deloitte',
            'pwc.com': 'PwC',
            'kpmg.com': 'KPMG',
            'ey.com': 'Ernst & Young'
        };
        
        return firmMappings[domain] || domain.split('.')[0];
    }

    updateDisplay() {
        if (this.isMobile) {
            this.updateMobileDisplay();
        } else {
            this.updateDesktopDisplay();
        }
    }

    updateDesktopDisplay() {
        // Update podium
        const podiumContainer = document.getElementById('podium-container');
        if (podiumContainer && this.leaderboardData.length >= 3) {
            const topThree = this.leaderboardData.slice(0, 3);
            const podiumOrder = [topThree[1], topThree[0], topThree[2]]; // 2nd, 1st, 3rd
            const positions = ['second', 'first', 'third'];
            const heights = ['120px', '140px', '100px'];
            
            podiumContainer.innerHTML = podiumOrder.map((player, index) => {
                if (!player) return '';
                
                const position = positions[index];
                const height = heights[index];
                const rank = position === 'first' ? '1st' : position === 'second' ? '2nd' : '3rd';
                
                return `
                    <div class="podium-position podium-${position}" style="display: flex; flex-direction: column; align-items: center;">
                        <div class="podium-player">
                            <div class="podium-avatar">${player.initials}</div>
                            <div class="podium-name">${player.name}</div>
                            <div class="podium-firm">${player.firm}</div>
                            <div class="podium-score">${player.score.toLocaleString()}</div>
                        </div>
                        <div class="podium-base" style="height: ${height};">${rank}</div>
                    </div>
                `;
            }).join('');
        }
        
        // Update table
        const tableContent = document.getElementById('leaderboard-content');
        if (tableContent) {
            console.log('🏆 HallOfFameTemplate: Updating table with', this.leaderboardData.length, 'players');
            const tableHTML = this.leaderboardData.map(player => `
                <div class="table-row">
                    <div class="rank-badge ${player.rank === 1 ? 'rank-gold' : player.rank === 2 ? 'rank-silver' : player.rank === 3 ? 'rank-bronze' : ''}">${player.rank}</div>
                    <div class="player-info">
                        <div class="player-avatar">${player.initials}</div>
                        <div class="player-name">${player.name}</div>
                    </div>
                    <div class="firm-badge">${player.firm}</div>
                    <div class="score-display">${player.score.toLocaleString()}</div>
                    <div class="trend-indicator trend-${player.trend > 0 ? 'up' : player.trend < 0 ? 'down' : 'same'}">
                        ${player.trend > 0 ? '↗' : player.trend < 0 ? '↘' : '→'}
                    </div>
                </div>
            `).join('');
            
            console.log('🏆 HallOfFameTemplate: Setting table HTML length:', tableHTML.length);
            tableContent.innerHTML = tableHTML;
            console.log('✅ HallOfFameTemplate: Table updated successfully');
        }
    }

    updateMobileDisplay() {
        const mobileContent = document.getElementById('mobile-leaderboard-content');
        if (mobileContent) {
            mobileContent.innerHTML = this.leaderboardData.map(player => `
                <div style="
                    background: rgba(30, 41, 59, 0.6);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 16px;
                    padding: 20px;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                ">
                    <div class="rank-badge ${player.rank === 1 ? 'rank-gold' : player.rank === 2 ? 'rank-silver' : player.rank === 3 ? 'rank-bronze' : ''}">${player.rank}</div>
                    <div class="player-avatar">${player.initials}</div>
                    <div style="flex: 1;">
                        <div class="player-name">${player.name}</div>
                        <div style="font-size: 12px; color: rgba(255, 255, 255, 0.6);">${player.firm}</div>
                    </div>
                    <div class="score-display">${player.score.toLocaleString()}</div>
                </div>
            `).join('');
        }
    }

    setupEventListeners() {
        if (!this.domElement) return;
        
        // Desktop filters
        const gameFilter = document.getElementById('game-filter');
        const modeFilter = document.getElementById('mode-filter');
        
        if (gameFilter) {
            gameFilter.addEventListener('change', (e) => {
                this.currentFilters.game = e.target.value;
                this.loadLeaderboardData();
            });
        }
        
        if (modeFilter) {
            modeFilter.addEventListener('change', (e) => {
                this.currentFilters.mode = e.target.value;
                this.loadLeaderboardData();
            });
        }
        
        // Mobile filters
        const mobileGameFilter = document.getElementById('mobile-game-filter');
        const mobileModeFilter = document.getElementById('mobile-mode-filter');
        
        if (mobileGameFilter) {
            mobileGameFilter.addEventListener('change', (e) => {
                this.currentFilters.game = e.target.value;
                this.loadLeaderboardData();
            });
        }
        
        if (mobileModeFilter) {
            mobileModeFilter.addEventListener('change', (e) => {
                this.currentFilters.mode = e.target.value;
                this.loadLeaderboardData();
            });
        }
    }

    generateInitials(name) {
        if (!name) return 'AN';
        return name.split(' ').map(word => word.charAt(0).toUpperCase()).join('').substring(0, 2);
    }

    generateTrend() {
        const trends = [-2, -1, 0, 1, 2];
        return trends[Math.floor(Math.random() * trends.length)];
    }

    showErrorState() {
        const mainContent = this.domElement.querySelector('.main-content');
        if (mainContent) {
            mainContent.innerHTML = `
                <div style="
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    text-align: center;
                ">
                    <div style="font-size: 64px; margin-bottom: 24px;">⚠️</div>
                    <h2 style="font-size: 24px; font-weight: 600; margin-bottom: 16px;">Unable to Load Rankings</h2>
                    <p style="color: rgba(255, 255, 255, 0.6); margin-bottom: 32px;">Please check your connection and try again.</p>
                    <button onclick="location.reload()" style="
                        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                        border: none;
                        color: #0f172a;
                        padding: 12px 24px;
                        border-radius: 8px;
                        font-size: 14px;
                        font-weight: 600;
                        cursor: pointer;
                    ">Reload Page</button>
                </div>
            `;
        }
    }

    shutdown() {
        console.log('🏆 HallOfFameSceneTemplate: Shutting down...');
        
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        
        const overlay = document.getElementById('hall-of-fame-template-overlay');
        if (overlay) {
            overlay.remove();
        }
        
        super.shutdown();
    }
}
